/**
 * 26 Oct 2026 (D-1026-HOUSEHOLD) — how an event counts a household, the office's way to change a count, and a new person
 * added from an event.
 *
 *   Count the whole household     the household size on the tenant's card is the most they can bring, and what a new RSVP starts at
 *                                 (in-house, auditorium and community events)
 *   Limited tickets: N per household
 *                                 the count stops at N (and never above the household) — outings, ball games
 *
 * The choice and N live in the event's own file (`cal.countMode`, `cal.ticketsPerHousehold`) — no SharePoint column
 * (MaxPerHousehold is a Raffle-only column in the schema). An event with nothing stored takes the mode its kind gives.
 * The tenant's Household size is in the person's own file already (1.5.2); it is not a column either.
 */
import { z } from 'zod';
import { householdCap } from './events';

export const COUNT_MODES = ['household', 'limited'] as const;
export type CountMode = (typeof COUNT_MODES)[number];
export const LIMITED_DEFAULT = 2;
export const TICKETS_MAX = 20;

/** What an event with nothing stored counts by: an outing is limited tickets; every other kind counts the whole household. */
export const defaultCountMode = (kind: string | undefined | null): CountMode => (kind === 'outing' ? 'limited' : 'household');

export interface CountRuleInput {
  mode: CountMode;
  /** N for limited tickets (null = the default, 2). */
  per: number | null | undefined;
  /** The tenant's household size (1–4, unset = 1). */
  size: number;
  /** Extra spots the office offered this household after the RSVP close. */
  extra?: number;
}
/** The most this household may say for this event, and the number a new RSVP starts at (the two are the same). */
export function countRule(i: CountRuleInput): { cap: number; defaultCount: number; per: number | null } {
  const size = Math.max(1, i.size);
  if (i.mode === 'household') return { cap: size, defaultCount: size, per: null };
  const per = i.per && i.per > 0 ? i.per : LIMITED_DEFAULT;
  const cap = householdCap(per, size, i.extra ?? 0);
  return { cap, defaultCount: cap, per };
}

/** The office changes how many are coming on a line. */
export const CountsSchema = z.object({
  rsvpId: z.string().trim().min(5).max(40),
  household: z.number().int().min(1).max(30),
  guests: z.number().int().min(0).max(10),
  /** Needed (and kept) only when the new numbers go past a limit; the words are the office's own. */
  reason: z.string().trim().max(200).optional().default(''),
});
export type CountsInput = z.input<typeof CountsSchema>;

export const OVER_KINDS = ['household', 'guests', 'capacity'] as const;
export type OverKind = (typeof OVER_KINDS)[number];

/** One time the office went past a limit, as the event's file keeps it. */
export interface CountOverride {
  at: string;
  by: string;
  reason: string;
  over: OverKind[];
  from: { household: number; guests: number };
  to: { household: number; guests: number };
}

/** Someone who is not on the roster yet, added from an event's Add people. */
export const NewPersonSchema = z.object({
  firstName: z.string().trim().min(1, 'required').max(60),
  lastName: z.string().trim().min(1, 'required').max(60),
  unit: z.string().trim().min(1, 'required').max(20),
  phone: z.string().trim().max(40).optional().default(''),
  email: z.string().trim().max(255).email().or(z.literal('')).optional().default(''),
  language: z.enum(['English', 'Spanish', 'Other']).optional().default('English'),
  /** How many from the household are coming, the person included (the household size on the card is set to match). */
  household: z.number().int().min(1).max(4).optional().default(1),
  /** After the duplicate check showed someone close: "this is a different person". */
  notDuplicate: z.boolean().optional().default(false),
});
export type NewPersonInput = z.input<typeof NewPersonSchema>;

export interface DuplicateHit {
  tenantId: string;
  name: string;
  unit: string;
  /** Tenant · Moved out · Staff unit … */
  detail: string;
  /** Already holds a place on this event. */
  onEvent: boolean;
}
export type NewPersonResult =
  | { status: 'duplicate'; matches: DuplicateHit[] }
  | { status: 'added'; tenantId: string; message: string; row: import('./events').RsvpRowView };

const squash = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
const unitKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

function distance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[n]!;
}

/**
 * The same apartment and a name that could be the same person: the same words, a typo or two, a nickname's first
 * letters ("Bob" / "Robert" is not caught — the office sees the apartment's list), or the same last name with a first name
 * that starts the same way.
 */
export function similarPerson(a: { first: string; last: string; unit: string }, b: { first: string; last: string; unit: string }): boolean {
  if (unitKey(a.unit) !== unitKey(b.unit)) return false;
  const fa = squash(a.first);
  const fb = squash(b.first);
  const la = squash(a.last);
  const lb = squash(b.last);
  const full = (f: string, l: string) => `${f} ${l}`.trim();
  if (distance(full(fa, la), full(fb, lb)) <= 2) return true;
  if (la === lb && fa && fb && (fa.startsWith(fb.slice(0, 2)) || fb.startsWith(fa.slice(0, 2)))) return true;
  return distance(la, lb) <= 1 && distance(fa, fb) <= 1;
}

/** The warning, in words, for the numbers that go past a limit. */
export function overWords(o: { kinds: OverKind[]; household: number; householdCap: number; guests: number; guestsAllowed: number; limit: number | null; taken: number }): string {
  const parts: string[] = [];
  if (o.kinds.includes('household')) parts.push(`${o.household} from the household is more than the ${o.householdCap} this event allows for it`);
  if (o.kinds.includes('guests')) parts.push(`${o.guests} guest${o.guests === 1 ? '' : 's'} is more than the ${o.guestsAllowed} this event allows`);
  if (o.kinds.includes('capacity') && o.limit !== null) parts.push(`this would be ${o.taken} of ${o.limit} places — over the room`);
  return parts.join('; ');
}
